// hooks/useCourseCreationFlow.js
import { useState, useCallback } from 'react';
import {
    useCreateCourseMutation,
    useUpdateCourseMutation,
    useUploadToS3Mutation,
} from '../../store/services/university-service';
import { pollForCourseReady } from '../../utils/CoursePolling';

const CREATION_STATES = {
    EDITING: 'editing',
    CREATING_COURSE: 'creating_course',
    UPLOADING_CONTENT: 'uploading_content',
    UPLOADING_MEDIA: 'uploading_media',
    PROCESSING: 'processing',
    COMPLETE: 'complete',
    ERROR: 'error',
};

export const useCourseCreationFlow = (mode = 'create') => {
    const [state, setState] = useState(CREATION_STATES.EDITING);
    const [error, setError] = useState(null);
    const [createdCourseId, setCreatedCourseId] = useState(null);
    const [completedSteps, setCompletedSteps] = useState([]);

    const [createCourse] = useCreateCourseMutation();
    const [updateCourse] = useUpdateCourseMutation();
    const [uploadToS3] = useUploadToS3Mutation();

    const markStepComplete = useCallback((step) => {
        setCompletedSteps(prev => [...new Set([...prev, step])]);
    }, []);

    const executeCreationFlow = useCallback(async (formData, pendingFiles, existingCourseId = null) => {
        try {
            setError(null);
            let courseId = existingCourseId;

            // Step 1: Create or update course metadata
            if (!completedSteps.includes('create_course')) {
                setState(CREATION_STATES.CREATING_COURSE);

                if (mode === 'create') {
                    const response = await createCourse({
                        name: formData.courseName,
                        courseName: formData.courseName,
                        languageCodes: formData.languageCodes || [],
                        // Set defaults - hidden from user
                        scoredOutOf: 100,
                        format: 'SCORM',
                        published: formData.published || false,
                        sharedTenancyIds: formData.sharedTenancyIds || [],
                        publishToFamily: formData.publishToFamily || false,
                        linkedTypeformCourse: '',
                        isLms: true,
                    }).unwrap();

                    courseId = response.createdCourse.internalId;
                    setCreatedCourseId(courseId);
                } else {
                    await updateCourse({
                        courseData: {
                            universityCourseId: courseId,
                            name: formData.courseName,
                            courseName: formData.courseName,
                            languageCodes: formData.languageCodes || [],
                            scoredOutOf: formData.scoredOutOf || 100,
                            format: formData.format || 'SCORM',
                            published: formData.published || false,
                            sharedTenancyIds: formData.sharedTenancyIds || [],
                            publishToFamily: formData.publishToFamily || false,
                            linkedTypeformCourse: formData.linkedTypeformCourse || '',
                            isLms: pendingFiles?.content ? true : (formData.isLms || false),
                        }
                    }).unwrap();
                }

                markStepComplete('create_course');
            }

            // Step 2: Upload content ZIP if provided
            if (pendingFiles.content && !completedSteps.includes('upload_content')) {
                setState(CREATION_STATES.UPLOADING_CONTENT);

                await uploadToS3({
                    fileType: 'application/zip',
                    id: courseId,
                    file: pendingFiles.content,
                    courseType: "lms"
                }).unwrap();

                markStepComplete('upload_content');
            }

            // Step 3: Upload media files in parallel
            if ((pendingFiles.image || pendingFiles.gif) && !completedSteps.includes('upload_media')) {
                setState(CREATION_STATES.UPLOADING_MEDIA);

                const uploadPromises = [];

                const mediaCourseType = (pendingFiles.content || formData.isLms) ? "lms" : "original";

                if (pendingFiles.image) {
                    uploadPromises.push(
                        uploadToS3({
                            fileType: 'image/png',
                            id: courseId,
                            file: pendingFiles.image,
                            courseType: mediaCourseType
                        }).unwrap()
                    );
                }

                if (pendingFiles.gif) {
                    uploadPromises.push(
                        uploadToS3({
                            fileType: 'image/gif',
                            id: courseId,
                            file: pendingFiles.gif,
                            courseType: mediaCourseType
                        }).unwrap()
                    );
                }

                await Promise.all(uploadPromises);
                markStepComplete('upload_media');
            }

            // Step 4: Poll for processing completion if content was uploaded
            if (pendingFiles.content && !completedSteps.includes('processing')) {
                setState(CREATION_STATES.PROCESSING);

                await pollForCourseReady(courseId);
                markStepComplete('processing');
            }

            setState(CREATION_STATES.COMPLETE);
            return { success: true, courseId };

        } catch (err) {
            console.error('Course creation/update flow error:', err);
            setError(err.data?.message || err.message || 'An error occurred');
            setState(CREATION_STATES.ERROR);
            return { success: false, error: err };
        }
    }, [mode, createCourse, updateCourse, uploadToS3, completedSteps, markStepComplete]);

    const reset = useCallback(() => {
        setState(CREATION_STATES.EDITING);
        setError(null);
        setCreatedCourseId(null);
        setCompletedSteps([]);
    }, []);

    return {
        state,
        error,
        createdCourseId,
        executeCreationFlow,
        reset,
        isProcessing: state !== CREATION_STATES.EDITING && state !== CREATION_STATES.COMPLETE && state !== CREATION_STATES.ERROR,
        isComplete: state === CREATION_STATES.COMPLETE,
        isError: state === CREATION_STATES.ERROR,
        STATES: CREATION_STATES,
    };
};